import React, { PureComponent } from "react";
import { connect } from "react-redux";
import ReactResizeDetector from "react-resize-detector";
import {
  SideBarContents,
  SideBarContainer,
  SideBarRoot,
  SideBarTitle,
  SideBarScrollContainer,
  SideBarHeaderArea,
} from "../common/StyledComponents";
import QuickFilterPanel from "../filters/QuickFilterPanel";
import SideBarColumns from "./SideBarColumns";
import { EXPAND_COLUMN_WIDTH, VIEWPORT_TITLE_HEIGHT } from "../../helpers/Const";
import { DnDSideListRow } from "./TaskRow";
import { ViewType } from "src/backend/constants/viewTypes";
import { translateMFilesString } from "../../../backend/helpers/translationHelpers";
import { iRootState } from "src/rematch/store";
import { IObjectHierarchyItem } from "src/backend/interfaces/configuration/IConfiguration";
import { IHierarchyItem } from "src/backend/interfaces/IItem";

interface ITaskListProps {
  width: number;
  taskHeight: number;
  viewName: (viewIndex: number) => string;
  translationKey: (viewIndex: number) => string;
  objectHierarchy: (viewIndex: number) => Map<number,IObjectHierarchyItem>;
  objectTypeColumns: (objectId: number) => (string | number)[];
  expandedTasks: Set<number>;
  visibleTaskColumns: { name: string, width: number }[];
  dragUnderway: boolean;
  onToggleTaskExpand: (taskId: number) => void;
  viewType?: string;
  viewIndex: number;
  endRow: number;
  visibleData: IHierarchyItem[];
  onScroll: (scrollTop: number) => void;
  scrollTop: number;
  sortOrder: { columnName: string, isAscending: boolean }[];
  onColumnResize: (columnIndex: number, value: number) => void;
  onAddSortColumn: (columnName: string) => void;
  onToggleExpandAll: (columnName: string) => void;
  onListVerticalSizeUpdate: (width: number, height: number) => void;
  taskListExtraHeight: number;
}

interface ITaskListState {
  sideBarInnerWidth: number;
}

class TaskList extends PureComponent<ITaskListProps,ITaskListState> {

  scrollRef: React.RefObject<HTMLDivElement>;
  scrollRef2: React.RefObject<HTMLDivElement>;
  resizeRef: string;
  resizeRef2: string;

  constructor(props: ITaskListProps) {
    super(props);

    this.scrollRef = React.createRef();
    this.scrollRef2 = React.createRef();
    const rnd = Math.floor((Math.random() * 1000000) + 1);
    this.resizeRef = `tlrf${rnd}-1`;
    this.resizeRef2 = `tlrf${rnd}-2`;
    this.state = {
      sideBarInnerWidth: this.props.width,
    };
  }

  getTaskRowItem(task: IHierarchyItem, rowIndex: number, activeResourceId: number) {
    const rowProps = {
      key: rowIndex,
      task: task,
      hasChildren: task.children.length > 0,
      isExpanded: this.props.expandedTasks.has(task.id),
      visibleTaskColumns: this.props.visibleTaskColumns,
      dragUnderway: this.props.dragUnderway,
      onToggleExpand: this.props.onToggleTaskExpand,
      checkedOutToSomeoneElse: task.isCheckedOut && !task.isCheckedOutToThisUser,
      isResourcedTask: task.hierarchyLevel > 0 && this.props.viewType === ViewType.Resourcing,
      resourceId: activeResourceId,
      viewIndex: this.props.viewIndex,
    };
    return <DnDSideListRow {...rowProps}></DnDSideListRow>;
  }

  renderTaskRows() {
    const result = [];
    let activeResourceId: number;
    // TODO: Investigate whether the row range limiting optimisation is actually useless.
    for (let i = 0; i < this.props.endRow; i++) {
      const task = this.props.visibleData[i];
      if (task.hierarchyLevel === 0 && this.props.viewType === ViewType.Resourcing) {
        activeResourceId = task.id;
      }
      result.push(this.getTaskRowItem(task, i, activeResourceId));
    }
    return result;
  }
  doScroll = () => {
    this.props.onScroll(this.scrollRef.current.scrollTop);
  };

  updateScroll = () => {
    if (this.scrollRef.current.scrollTop !== this.props.scrollTop) {
      this.scrollRef.current.scrollTop = this.props.scrollTop;
    }
  };

  componentDidMount() {
    this.updateScroll();
  }

  componentDidUpdate() {
    this.updateScroll();
  }

  onHorizontalInnerResize = (width: number, _height: number) => {
    this.setState(() => {
      return { sideBarInnerWidth: width };
    });
  };

  getColumnPropertyMap() {
    const headers = new Map();
    const objectHierarchy = this.props.objectHierarchy(this.props.viewIndex);
    for(const hierarchyItem of objectHierarchy.values()) {
      const objectId = hierarchyItem.id;
      const objectTypeColumns = this.props.objectTypeColumns(objectId);
      headers.set(objectId,objectTypeColumns);
    }
    return headers;
  }

  render() {
    let innerWidth = EXPAND_COLUMN_WIDTH;
    for (const column of this.props.visibleTaskColumns) {
      innerWidth += column.width;
    }

    let innerHeight = this.props.taskHeight * this.props.visibleData.length + this.props.taskListExtraHeight;
    const isFirefox = navigator.userAgent.indexOf("Firefox") > -1;
    if(isFirefox) {
      innerHeight += 17; //For some reason Firefox doesn't calculate scrollbar size
    }

    let viewName = this.props.viewName(this.props.viewIndex);
    const translationKey = this.props.translationKey(this.props.viewIndex);
    if(translationKey !== undefined && translationKey != null) {
      viewName = translateMFilesString(translationKey, viewName);
    }

    return (
      <SideBarRoot>
        <SideBarHeaderArea style={{width: this.props.width, flexBasis: VIEWPORT_TITLE_HEIGHT}}>
          <SideBarTitle>{viewName}</SideBarTitle>
          <QuickFilterPanel viewIndex={this.props.viewIndex} />
        </SideBarHeaderArea>
        <SideBarScrollContainer className="sidebar-columns" width={this.props.width}>
          <SideBarContainer width={innerWidth} onScroll={this.doScroll} id={this.resizeRef} ref={this.scrollRef}>
            <ReactResizeDetector handleHeight={true} onResize={this.props.onListVerticalSizeUpdate} querySelector={`#${this.resizeRef}`}></ReactResizeDetector>
            <SideBarColumns
              visibleColumns={this.props.visibleTaskColumns}
              columnPropertyMap={this.getColumnPropertyMap()}
              sortOrder={this.props.sortOrder}
              onAddSortColumn={this.props.onAddSortColumn}
              onToggleExpandAll={this.props.onToggleExpandAll}
              onColumnResize={this.props.onColumnResize}
              width={innerWidth}
              height={VIEWPORT_TITLE_HEIGHT - 1} // -1 due to bottom border
            ></SideBarColumns>
            <SideBarContents height={innerHeight} id={this.resizeRef2} ref={this.scrollRef2}>
              <ReactResizeDetector handleWidth={true} onResize={this.onHorizontalInnerResize} querySelector={`#${this.resizeRef2}`}></ReactResizeDetector>
              {this.renderTaskRows()}
            </SideBarContents>
          </SideBarContainer>
        </SideBarScrollContainer>
      </SideBarRoot>
    );
  }
}

const mapState = (state: iRootState) => ({
  taskHeight: state.UI.displaySettings.taskHeight,
  viewName: (viewIndex: number) => state.common.configuration.viewConfigurations[viewIndex].displayName,
  translationKey: (viewIndex: number) => state.common.configuration.viewConfigurations[viewIndex].translationKey,
  objectHierarchy: (viewIndex: number) => state.common.configuration.viewConfigurations[viewIndex].objectHierarchy,
  objectTypeColumns: (objectType: number) => {
    const userColumns = state.UI.displaySettings.gridColumns[objectType];
    if(userColumns) {
      return userColumns;
    }
    return state.common.configuration.objectTypeConfigurations.get(objectType)?.gridColumns;
  },
});

export default connect(mapState, null)(TaskList);
